import {
  Boxes,
  Container,
  HardDrive,
  Network,
  Route,
  Server,
  Shield,
  type LucideIcon,
} from "lucide-react";

import type { DeviceStatus, DeviceType } from "@/api/frameworksApi";

export type CiStatus = DeviceStatus;

export interface CiForm {
  orgId: string;
  name: string;
  type: DeviceType | "";
  status: CiStatus | "";
  description: string;
  ipAddress: string;
  managementIp: string;
  virtualIp: string;
  hostname: string;
  osName: string;
  osVersion: string;
  cpuCores: string;
  ramGb: string;
  diskGb: string;
  hypervisor: string;
  parentCiId: string;
  imageName: string;
  namespace: string;
  registryUrl: string;
  totalCapacityTb: string;
  usedCapacityTb: string;
  environment: string;
}

export type CiFormErrors = Partial<Record<keyof CiForm, string>>;

export const defaultForm: CiForm = {
  orgId: "",
  name: "",
  type: "",
  status: "",
  description: "",
  ipAddress: "",
  managementIp: "",
  virtualIp: "",
  hostname: "",
  osName: "",
  osVersion: "",
  cpuCores: "",
  ramGb: "",
  diskGb: "",
  hypervisor: "",
  parentCiId: "",
  imageName: "",
  namespace: "",
  registryUrl: "",
  totalCapacityTb: "",
  usedCapacityTb: "",
  environment: "",
};

export const DEFAULT_DEVICE_PAGE_SIZE = 100;

export const HYPERVISOR_OPTIONS = [
  { value: "VMware ESXi", label: "VMware ESXi" },
  { value: "Hyper-V", label: "Hyper-V" },
  { value: "KVM", label: "KVM" },
  { value: "Xen", label: "Xen" },
  { value: "Proxmox", label: "Proxmox" },
];

export const DEVICE_TYPE_LABELS: Record<string, string> = {
  SERVER: "Server",
  VM: "Virtual Machine",
  FIREWALL: "Firewall",
  SWITCH: "Switch",
  ROUTER: "Router",
  LB: "Load Balancer",
  CONTAINER: "Container",
  STORAGE: "Storage",
};

export const DEVICE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  MAINTENANCE: "Maintenance",
  DECOMMISSIONED: "Decommissioned",
};

export const DEFAULT_DEVICE_TYPES: DeviceType[] = [
  "SERVER",
  "VM",
  "FIREWALL",
  "SWITCH",
  "ROUTER",
  "LB",
  "CONTAINER",
  "STORAGE",
];

export const DEFAULT_DEVICE_STATUSES: DeviceStatus[] = [
  "ACTIVE",
  "INACTIVE",
  "MAINTENANCE",
  "DECOMMISSIONED",
];

export const DEFAULT_ENVIRONMENTS: string[] = ["PROD", "SIT", "DEV", "UAT"];

export const DEVICE_FILTER_COLUMN_IDS = new Set<string>([
  "application",
  "assetType",
  "environment",
  "operatingSystem",
]);

export interface TypeFieldConfig {
  ipAddress?: boolean;
  managementIp?: boolean;
  virtualIp?: boolean;
  hostname?: boolean;
  osName?: boolean;
  osVersion?: boolean;
  cpuCores?: boolean;
  ramGb?: boolean;
  diskGb?: boolean;
  hypervisor?: boolean;
  parentCiId?: "SERVER";
  imageName?: boolean;
  namespace?: boolean;
  registryUrl?: boolean;
  totalCapacityTb?: boolean;
  usedCapacityTb?: boolean;
}

export const TYPE_FIELDS: Record<DeviceType, TypeFieldConfig> = {
  SERVER: {
    ipAddress: true,
    hostname: true,
    osName: true,
    osVersion: true,
    cpuCores: true,
    ramGb: true,
    diskGb: true,
  },
  VM: {
    ipAddress: true,
    hostname: true,
    osName: true,
    osVersion: true,
    cpuCores: true,
    ramGb: true,
    diskGb: true,
    hypervisor: true,
    parentCiId: "SERVER",
  },
  FIREWALL: {
    ipAddress: true,
    managementIp: true,
    osName: true,
    osVersion: true,
  },
  SWITCH: { managementIp: true, osName: true, osVersion: true },
  ROUTER: {
    ipAddress: true,
    managementIp: true,
    osName: true,
    osVersion: true,
  },
  LB: { virtualIp: true, managementIp: true, osName: true, osVersion: true },
  CONTAINER: {
    imageName: true,
    namespace: true,
    parentCiId: "SERVER",
    registryUrl: true,
  },
  STORAGE: {
    osName: true,
    osVersion: true,
    totalCapacityTb: true,
    usedCapacityTb: true,
  },
};

export const DRAWER_TYPE_ICONS: Record<DeviceType, LucideIcon> = {
  SERVER: Server,
  VM: Boxes,
  FIREWALL: Shield,
  SWITCH: Network,
  ROUTER: Route,
  LB: Route,
  CONTAINER: Container,
  STORAGE: HardDrive,
};

export const DRAWER_TYPE_HEX: Record<DeviceType, string> = {
  SERVER: "#60a5fa",
  VM: "#c084fc",
  FIREWALL: "#f87171",
  SWITCH: "#fbbf24",
  ROUTER: "#fb923c",
  LB: "#34d399",
  CONTAINER: "#22d3ee",
  STORAGE: "#94a3b8",
};

export const DRAWER_STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
  INACTIVE: "bg-gray-500/15 text-gray-400 border border-gray-500/25",
  MAINTENANCE: "bg-amber-500/15 text-amber-400 border border-amber-500/25",
  DECOMMISSIONED: "bg-red-500/15 text-red-400 border border-red-500/25",
  RUNNING: "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25",
};

export const NODE_W = 200;
export const NODE_GAP = 56;
export const ROW_H = 260;

export const TOPO_EDGE_COLORS: Record<string, string> = {
  "runs on": "#818cf8",
  "connected to": "#34d399",
  behind: "#fb923c",
  "attached to": "#fbbf24",
  "protected by": "#f87171",
};

export const TOPO_TYPE_HEX: Record<string, string> = {
  SERVER: "#60a5fa",
  VM: "#c084fc",
  FIREWALL: "#f87171",
  SWITCH: "#fbbf24",
  ROUTER: "#fb923c",
  LB: "#34d399",
  CONTAINER: "#22d3ee",
  STORAGE: "#94a3b8",
};

export const TOPO_TYPE_COLORS: Record<string, string> = {
  SERVER: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  VM: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  FIREWALL: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  SWITCH:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
  ROUTER:
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  LB: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  CONTAINER: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400",
  STORAGE:
    "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400",
};

export const EDGE_LABEL_MAP: Record<string, string> = {
  HOSTED_ON: "runs on",
  CONNECTED_TO: "connected to",
  BEHIND: "behind",
  ATTACHED_TO: "attached to",
  PROTECTED_BY: "protected by",
};

export const REL_SECTION_LABELS: Record<
  string,
  { label: string; direction: "out" | "in" }
> = {
  hostedOn: { label: "Hosted On", direction: "out" },
  connectedTo: { label: "Connected To", direction: "out" },
  behind: { label: "Behind", direction: "out" },
  attachedTo: { label: "Attached To", direction: "out" },
  protectedBy: { label: "Protected By", direction: "out" },
  hosts: { label: "Hosts", direction: "in" },
  connectedFrom: { label: "Connected From", direction: "in" },
  backends: { label: "Backends", direction: "in" },
  attachedFrom: { label: "Attached From", direction: "in" },
  protects: { label: "Protects", direction: "in" },
};

export const DEVICE_SORT_COLUMNS: Record<string, string> = {
  name: "name",
  orgName: "orgName",
  type: "type",
  hostname: "hostname",
  ipAddress: "ipAddress",
  os: "osName",
  environment: "environment",
  application: "application",
  applicationNames: "applicationNames",
  vmLastSeenDate: "vmLastSeenDate",
  vmLastFetchDate: "vmLastFetchDate",
  cmLastSeenDate: "cmLastSeenDate",
  cmLastFetchDate: "cmLastFetchDate",
  status: "status",
};

export const PIN_HEAD = "sticky top-0 z-30 bg-[#eef2f7] dark:bg-[#262738]";
export const PIN_CELL =
  "sticky z-10 bg-white dark:bg-[#131320] group-hover:bg-[#f1f5fe] dark:group-hover:bg-[#23232f]";
export const PIN_EDGE =
  "after:pointer-events-none after:absolute after:inset-y-0 after:-right-px after:w-px " +
  "after:bg-[#e2e8f0] dark:after:bg-white/[0.06] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.3)]";

export const MORE_FIELDS_CONFIG = [
  { key: "hostname", label: "Hostname" },
  { key: "vmLastFetchDate", label: "VM Fetched" },
  { key: "cmLastFetchDate", label: "CM Fetched" },
] as const;

export const MORE_FIELDS_OPTIONS = MORE_FIELDS_CONFIG.map((c) => ({
  value: c.key,
  label: c.label,
}));

export const MORE_FIELDS_KEYS = new Set<string>(
  MORE_FIELDS_CONFIG.map((c) => c.key),
);

